import axios from 'axios';
import { getOrSetAnonymousId } from './simple-tracker';
import { isChunkLoadError } from './chunkLoadRecovery';

const TOKEN_KEY = 'hv_jwt';
const DEDUPE_MS = 5 * 60 * 1000;
const REPORT_PATH = '/api/error-events';

const dedupeCache = new Map();
let installed = false;

function uuidv4() {
  return crypto.randomUUID();
}

function getOrSetSessionId() {
  const k = 'hv_sessionId';
  let id = sessionStorage.getItem(k);
  if (!id) {
    id = uuidv4();
    sessionStorage.setItem(k, id);
  }
  return id;
}

function decodeJwtPayload(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // JWT uses base64url — plain atob() fails without this conversion
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    if (pad) base64 += '='.repeat(4 - pad);
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

function getUserIdFromToken() {
  const payload = decodeJwtPayload(getToken());
  if (!payload) return null;
  return payload.id || payload.sub || payload._id || payload.userId || null;
}

function getUserEmailFromToken() {
  const payload = decodeJwtPayload(getToken());
  if (!payload) return null;
  return payload.email || null;
}

function parseBrowserInfo() {
  const ua = navigator.userAgent || '';
  let browser = 'Unknown';
  let browserVersion = '';

  if (/Edg\//.test(ua)) {
    browser = 'Edge';
    browserVersion = ua.match(/Edg\/([\d.]+)/)?.[1] || '';
  } else if (/OPR\//.test(ua) || /Opera/.test(ua)) {
    browser = 'Opera';
    browserVersion = ua.match(/(?:OPR|Opera)\/([\d.]+)/)?.[1] || '';
  } else if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) {
    browser = 'Chrome';
    browserVersion = ua.match(/Chrome\/([\d.]+)/)?.[1] || '';
  } else if (/Safari\//.test(ua) && !/Chrome/.test(ua)) {
    browser = 'Safari';
    browserVersion = ua.match(/Version\/([\d.]+)/)?.[1] || '';
  } else if (/Firefox\//.test(ua)) {
    browser = 'Firefox';
    browserVersion = ua.match(/Firefox\/([\d.]+)/)?.[1] || '';
  }

  let os = 'Unknown';
  if (/Windows NT/.test(ua)) os = 'Windows';
  else if (/Mac OS X/.test(ua)) os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
  else if (/Linux/.test(ua)) os = 'Linux';

  const device = /Mobi|Android|iPhone|iPad|iPod/i.test(ua) ? 'Mobile' : 'Desktop';
  const screenSize = `${window.screen?.width || 0}x${window.screen?.height || 0}`;

  return { browser, browserVersion, os, device, screenSize, userAgent: ua };
}

function getPageContext() {
  return {
    pageUrl: window.location.href,
    pagePath: window.location.pathname,
    referrer: document.referrer || null,
    language: navigator.language || null,
  };
}

function getIdentity() {
  return {
    userId: getUserIdFromToken(),
    email: getUserEmailFromToken(),
    anonymousId: getOrSetAnonymousId(),
    sessionId: getOrSetSessionId(),
  };
}

function buildFingerprint(payload) {
  return [
    payload.errorType,
    payload.userId || payload.anonymousId,
    payload.apiPath || '',
    payload.pagePath || '',
    payload.statusCode || '',
    String(payload.errorMessage || '').slice(0, 200),
  ].join('|');
}

function shouldReport(fingerprint) {
  const now = Date.now();
  const last = dedupeCache.get(fingerprint);
  if (last && now - last < DEDUPE_MS) return false;
  dedupeCache.set(fingerprint, now);
  return true;
}

function isReportingEndpoint(url) {
  if (!url) return false;
  return String(url).includes(REPORT_PATH);
}

/** Google Analytics / ads collect — not HireVeda APIs. Blocked pings are status 0 noise. */
function shouldIgnoreTelemetryUrl(url) {
  if (!url) return false;
  const raw = String(url);
  if (raw.includes('/g/collect')) return true;
  if (/google-analytics|googletagmanager|googleadservices|doubleclick/i.test(raw)) {
    return true;
  }
  try {
    const parsed = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    if (parsed.pathname === '/g/collect' || parsed.pathname.startsWith('/g/collect')) return true;
    const host = parsed.hostname.toLowerCase();
    if (
      host === 'google-analytics.com' ||
      host.endsWith('.google-analytics.com') ||
      host === 'analytics.google.com' ||
      host === 'googletagmanager.com' ||
      host.endsWith('.googletagmanager.com') ||
      host === 'googleadservices.com' ||
      host.endsWith('.googleadservices.com') ||
      host.endsWith('.doubleclick.net')
    ) {
      return true;
    }
  } catch {
    // keep reporting if URL cannot be parsed
  }
  return false;
}

function extractApiPath(url) {
  if (!url) return null;
  try {
    const parsed = new URL(url, window.location.origin);
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return String(url).split('?')[0];
  }
}

const EMAIL_IN_PATH = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const OBJECT_ID_IN_PATH = /\b[a-f0-9]{24}\b/gi;

function normalizeApiPath(path) {
  if (!path) return path;
  let decoded = String(path);
  try {
    decoded = decodeURIComponent(decoded);
  } catch {
    // keep original if the path is not valid URI encoding
  }
  return decoded
    .replace(EMAIL_IN_PATH, ':email')
    .replace(OBJECT_ID_IN_PATH, ':id');
}

function pathnameOf(url) {
  if (!url) return '';
  try {
    return new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost').pathname;
  } catch {
    return String(url).split('?')[0];
  }
}

/** Expected client outcomes — not product bugs. 500s still report. */
function isExpectedClientError(url, statusCode) {
  const path = pathnameOf(url);
  if (statusCode === 0) {
    return (
      path === '/api/track' ||
      path.startsWith('/api/track/') ||
      path.endsWith('/api/track') ||
      path === '/api/analytics/summary' ||
      path.startsWith('/api/analytics/summary') ||
      path.endsWith('/analytics/summary')
    );
  }
  if (!statusCode) return false;
  if (statusCode === 404) {
    if (path.includes('/execedge-funnel/lead/by-email/')) return true;
    if (path.endsWith('/recruiters/get')) return true;
    if (path.includes('/candidates/lookup')) return true;
  }
  if (statusCode === 401 && (path === '/api/me' || path.endsWith('/api/me'))) {
    return true;
  }
  return false;
}

function extractErrorMessage(error, response) {
  if (response?.data) {
    const data = response.data;
    if (typeof data === 'string') return data.slice(0, 1000);
    if (typeof data.error === 'string') return data.error.slice(0, 1000);
    if (typeof data.message === 'string') return data.message.slice(0, 1000);
    try {
      return JSON.stringify(data).slice(0, 1000);
    } catch {
      return 'Unknown API error';
    }
  }
  if (error?.message) return String(error.message).slice(0, 1000);
  return 'Unknown error';
}

function extractResponseBody(response) {
  if (!response?.data) return null;
  try {
    const raw = typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
    return raw.slice(0, 1500);
  } catch {
    return null;
  }
}

function getApiEndpoint() {
  const isLocal = window.location.hostname === 'localhost';
  return isLocal ? 'http://localhost:4002/api/error-events' : REPORT_PATH;
}

export async function reportClientError(payload) {
  if (!payload?.errorMessage) return;

  const statusCode = payload.statusCode ?? null;
  if (statusCode !== null && statusCode >= 200 && statusCode < 300) return;

  const fullPayload = {
    ...getPageContext(),
    ...getIdentity(),
    ...parseBrowserInfo(),
    occurredAt: new Date().toISOString(),
    ...payload,
  };

  fullPayload.fingerprint = buildFingerprint(fullPayload);
  if (!shouldReport(fullPayload.fingerprint)) return;

  try {
    const token = getToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    await fetch(getApiEndpoint(), {
      method: 'POST',
      headers,
      body: JSON.stringify(fullPayload),
      keepalive: true,
    });
  } catch {
    // Never block UX for telemetry
  }
}

function reportApiFailure({ method, url, statusCode, error, response }) {
  if (isReportingEndpoint(url)) return;
  if (shouldIgnoreTelemetryUrl(url)) return;
  if (statusCode !== null && statusCode >= 200 && statusCode < 300) return;
  if (axios.isCancel?.(error)) return;
  if (isExpectedClientError(url, statusCode)) return;

  reportClientError({
    errorType: statusCode === 0 || !response ? 'network_error' : 'api_error',
    apiMethod: (method || 'GET').toUpperCase(),
    apiPath: normalizeApiPath(extractApiPath(url)),
    statusCode: statusCode ?? null,
    errorMessage: extractErrorMessage(error, response),
    responseBody: extractResponseBody(response),
    stackTrace: error?.stack || null,
  });
}

function installAxiosInterceptor() {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const config = error?.config || {};
      const response = error?.response;
      reportApiFailure({
        method: config.method,
        url: config.url,
        statusCode: response?.status ?? 0,
        error,
        response,
      });
      return Promise.reject(error);
    }
  );
}

function installFetchInterceptor() {
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (...args) => {
    const input = args[0];
    const init = args[1] || {};
    const url = typeof input === 'string' ? input : input?.url;
    const method = (init.method || (typeof input !== 'string' ? input?.method : null) || 'GET').toUpperCase();

    if (isReportingEndpoint(url)) {
      return originalFetch(...args);
    }

    try {
      const response = await originalFetch(...args);
      if (!response.ok) {
        let responseData = null;
        try {
          const clone = response.clone();
          const text = await clone.text();
          try {
            responseData = { data: JSON.parse(text) };
          } catch {
            responseData = { data: text };
          }
        } catch {
          responseData = null;
        }

        reportApiFailure({
          method,
          url,
          statusCode: response.status,
          error: new Error(`HTTP ${response.status}`),
          response: responseData,
        });
      }
      return response;
    } catch (error) {
      reportApiFailure({
        method,
        url,
        statusCode: 0,
        error,
        response: null,
      });
      throw error;
    }
  };
}

function installGlobalErrorHandlers() {
  window.addEventListener('error', (event) => {
    if (event.message === 'Script error.') return;
    if (isChunkLoadError(event.error || event.message)) return;
    reportClientError({
      errorType: 'js_error',
      errorMessage: event.message || 'Uncaught error',
      stackTrace: event.error?.stack || `${event.filename}:${event.lineno}:${event.colno}`,
      apiMethod: null,
      apiPath: null,
      statusCode: null,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    if (axios.isCancel?.(reason)) return;
    if (reason?.code === 'ERR_CANCELED' || reason?.name === 'CanceledError') return;
    if (isChunkLoadError(reason)) return;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === 'string'
          ? reason
          : 'Unhandled promise rejection';

    reportClientError({
      errorType: 'promise_rejection',
      errorMessage: message,
      stackTrace: reason instanceof Error ? reason.stack : null,
      apiMethod: null,
      apiPath: null,
      statusCode: null,
    });
  });
}

export function reportReactError(error, componentStack) {
  reportClientError({
    errorType: 'react_error',
    errorMessage: error?.message || 'React render error',
    stackTrace: error?.stack || null,
    componentStack: componentStack || null,
    apiMethod: null,
    apiPath: null,
    statusCode: null,
  });
}

export function installErrorReporter() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  installAxiosInterceptor();
  installFetchInterceptor();
  installGlobalErrorHandlers();
}
